import { Component, Input, inject, OnDestroy } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { AuthService } from '../../../auth/auth.service';
import { Tournament } from '../../../_shared/models/tournament.model';
import { RossService } from '../../../scoreboard/ross/ross.service';
import { ToastService } from '../../../_core/services/toast.service';
import { ModalController } from '@ionic/angular';
import { ManageScoreSubscriptionComponent } from '../../../scoreboard/manage-score-subscription/manage-score-subscription.component';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { TMX_EXPORT_PDF_URL } from '../../../../environments/environment';
import { TournamentDetailsService } from '../tournament-details.service';
import { TournamentNotificationComponent } from '../tournament-notification/tournament-notification.component';

@Component({
  standalone: false,
    selector: 'app-tournament-menu',
    templateUrl: './tournament-menu.component.html',
    styleUrls: ['./tournament-menu.component.scss'],
})
export class TournamentMenuComponent implements OnDestroy {
    public authService = inject(AuthService);
    public popoverController = inject(PopoverController);
    private modalController = inject(ModalController);
    private rossService = inject(RossService);
    private toastService = inject(ToastService);
    private httpBackend = inject(HttpBackend); // Bypass interceptors for external requests
    private translateService = inject(TranslateService);
    private tournamentDetailsService = inject(TournamentDetailsService);
    
    // Create a separate HttpClient that bypasses interceptors for external API calls
    private externalHttpClient = new HttpClient(this.httpBackend);

    @Input() tournament: Tournament;
    @Input() selectedDate: string;
    @Input() showExportPdf: number;
    
    isExportingPdf = false;
    isTogglingAnnouncement = false;
    private destroyed$ = new Subject<void>();
    
    allowScoreSubmission(ev) {
        if(!ev.detail.checked) { 
            this.tournament.allowAutoSubmitScore = false;
        }
        this.popoverController.dismiss({allowResultSubmissionToPartner: ev.detail.checked });
    }

    public allowAutoScoreSubmissionToTmx(ev) {
        this.popoverController.dismiss({allowAutoScoreSubmissionToTmx: ev.detail.checked });
    }

    canSubscribeToTournamentScore(): boolean {
        const authUser = this.authService.authUserSignal();
        if (!authUser) return false;
        
        // Admin users (role = 500) can always subscribe
        if (authUser.role?.value === 500) return true;
        
        // Users with allowMatchSubscription can subscribe (this property exists on the auth user)
        return (authUser as any).allowMatchSubscription === true;
    }

    async subscribeToScoreUpdates() {
        const modal = await this.modalController.create({
            component: ManageScoreSubscriptionComponent,
            componentProps: {
                match: this.tournament, // Pass tournament as match object
                tournamentId: this.tournament._id,
                allowSubscribingToScore: true
            },
            presentingElement: document.querySelector('ion-router-outlet') || undefined
        });

        await modal.present();
    }

    dismiss(action: string) {
        this.popoverController.dismiss(action);
    }

    toggleAnnouncement(ev: any) {
        const enabled = ev.detail.checked;
        const message = this.tournament.announcement?.message || '';
        
        this.isTogglingAnnouncement = true;
        this.tournamentDetailsService
            .updateAnnouncement(this.tournament._id, message, enabled)
            .pipe(
                takeUntil(this.destroyed$),
                finalize(() => this.isTogglingAnnouncement = false)
            )
            .subscribe({
                next: () => {
                    this.tournament.announcement = { message, enabled };
                    this.toastService.presentToast(
                        this.translateService.instant(enabled ? 'Announcement enabled' : 'Announcement disabled')
                    );
                },
                error: (err) => {
                    const errorMessage = err.error?.message || this.translateService.instant('Failed to update announcement');
                    this.toastService.presentDangerToast(errorMessage);
                    // Revert toggle state on error
                    this.tournament.announcement = { message, enabled: !enabled };
                }
            });
    }

    async openNotificationModal() {
        await this.popoverController.dismiss();
        
        const modal = await this.modalController.create({
            component: TournamentNotificationComponent,
            componentProps: {
                tournament: this.tournament
            },
            presentingElement: document.querySelector('ion-router-outlet') || undefined
        });

        await modal.present();

        const { data } = await modal.onWillDismiss();
        if (data?.updated) {
            this.tournament.notification = data.notification;
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    async exportScheduleToPdf() {
        
        if (!this.tournament?._id || !this.selectedDate || !TMX_EXPORT_PDF_URL) {
            this.toastService.presentDangerToast(this.translateService.instant('Failed to export schedule to PDF'));
            return;
        }
        this.isExportingPdf = true;
        const payload = {
            tournamentId: this.tournament.tournamentId,
            tournament_name: this.tournament?.name,
            tournament_link: window?.location?.href || '',
            matchUpFilters: {
                scheduledDates: [this.selectedDate]
            }
        };

        this.externalHttpClient.post(TMX_EXPORT_PDF_URL, payload, {
            responseType: 'blob', // Handles application/pdf response from Lambda
        })
        .pipe(
            takeUntil(this.destroyed$),
            finalize(() => {
                this.isExportingPdf = false;
            })
        )
        .subscribe({
            next: (blob: Blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `Schedule-${this.tournament?.name || 'tournament'}-${this.selectedDate}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
                this.toastService.presentToast(this.translateService.instant('Schedule exported successfully'));
                this.popoverController.dismiss();
            },
            error: (error) => {
                console.error('Error exporting PDF:', error);
                this.toastService.presentDangerToast(this.translateService.instant('Failed to export schedule to PDF'));
            }
        });
    }

}
